/**
 * Shared contract between the Worker API (`@huddle/api`) and the PWA (`@huddle/web`).
 *
 * Everything here is structural types only — no runtime code — so the web bundle
 * pays nothing for importing it.
 *
 * All timestamps are epoch milliseconds, UTC. The workspace timezone is applied
 * only at render time (see `formatInZone` in the web app).
 */

/**
 * Crew roles, most senior first.
 *
 * Ranked rather than a flat set, because in a restaurant seniority genuinely
 * is a line: anything a TL can do, a manager can do. Encoding that as a rank
 * means a new capability is one number, not five boolean columns — and it
 * makes "who can do this" answerable by reading one table instead of grepping
 * for role checks.
 */
export type Role = 'owner' | 'manager' | 'tlmic' | 'tl' | 'associate';

export const ROLE_RANK: Record<Role, number> = {
  owner: 4,
  manager: 3,
  tlmic: 2,
  tl: 1,
  associate: 0,
};

export const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner',
  manager: 'Manager',
  tlmic: 'TL MIC',
  tl: 'Team Lead',
  associate: 'Associate',
};

/** Ordered for pickers: most senior first. */
export const ROLES: Role[] = ['owner', 'manager', 'tlmic', 'tl', 'associate'];

/**
 * The minimum rank each capability requires.
 *
 * One place to look, and one place to change. Deleting a channel is
 * deliberately owner-only: it is the single action in the app with no undo and
 * no partial recovery.
 */
export const CAPABILITY: Record<string, Role> = {
  /** Permanently destroy a channel and everything in it. No undo. */
  deleteChannel: 'owner',
  /**
   * Wipe the activity log.
   *
   * The log exists to answer "who changed that", so being able to erase it is
   * a real trade — which is why the clear leaves one entry behind saying it
   * happened. A log that can go silently empty is a weaker record than one
   * that admits it was cleared.
   */
  clearAuditLog: 'owner',
  /**
   * Erase an account entirely, rather than deactivating it.
   *
   * Only ever applies to an account with nothing attached to it — a mistyped
   * invite, a duplicate, a test. For anyone who actually took part,
   * deactivating is the answer: it removes their access while leaving what
   * they wrote where the people they were talking to can still read it.
   */
  deleteMember: 'owner',
  /**
   * Wipe a channel's messages, or a recent window of them, permanently.
   *
   * Distinct from the retention sweep (automatic, age-based, spares pins) and
   * from deleting the channel (takes the channel and its membership with it).
   * This empties a channel that stays. No undo, so: owner.
   */
  clearMessages: 'owner',
  /**
   * Delete a message that has replies, taking the replies with it.
   *
   * Deleting an ordinary message removes your own writing, or is a moderation
   * call on one other person's. Deleting a thread removes everyone who
   * answered, so it sits with deleting a channel rather than with deleting a
   * message.
   */
  deleteThread: 'owner',
  /** Change someone's role, deactivate them, rename the workspace. */
  manageCrew: 'manager',
  /** Add someone new to the crew. */
  inviteMembers: 'tlmic',
  /** Create, rename, archive a channel; set retention; edit membership. */
  manageChannels: 'tlmic',
  /** Post in an announcement channel. */
  postAnnouncements: 'tlmic',
  /**
   * Delete someone else's message.
   *
   * Owner-only by decision, not by convention. Everyone can always delete
   * their own — that check is separate and lives on authorship, not on rank —
   * so this is purely the power to remove what somebody else wrote. In a crew
   * this size that is worth keeping in one pair of hands.
   */
  moderateMessages: 'owner',
  /**
   * Pin and unpin.
   *
   * Deliberately higher than moderateMessages: a pinned message is exempt
   * from the retention sweep, so pinning decides what survives a channel's
   * retention window. That belongs with whoever sets the window.
   */
  pinMessages: 'manager',
} as const;

export function can(role: Role, capability: keyof typeof CAPABILITY | string): boolean {
  const required = CAPABILITY[capability];
  if (!required) return false;
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

export function atLeast(role: Role, minimum: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
export type MemberStatus = 'active' | 'deactivated';
export type ChannelType = 'standard' | 'announcement' | 'dm';
export type NotifyPref = 'all' | 'mentions' | 'muted';

export interface Workspace {
  id: string;
  name: string;
  /** IANA zone, e.g. "America/Chicago". Render-time only; storage is always UTC. */
  timezone: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatarKey: string | null;
  createdAt: number;
}

/** The caller's own identity, returned by GET /api/me. */
export interface Me {
  user: User;
  workspace: Workspace;
  role: Role;
  /** True once this user has at least one live push subscription. */
  pushEnabled: boolean;
  /** True when a password is set, so the UI offers "change" rather than "set". */
  hasPassword: boolean;
  /**
   * True when this session signed in with a code or link rather than a
   * password, so a new password can be set without knowing the old one. The UI
   * hides the "current password" field in that case — asking someone mid-reset
   * for the secret they forgot is a dead end.
   */
  canResetPassword: boolean;
  /**
   * True when a manager set this password rather than the user. The app keeps
   * prompting them to choose their own until they do.
   */
  mustChangePassword: boolean;
}

/** A user as seen inside a channel — enough to render them with their rank. */
export interface ChannelMember extends User {
  role: Role;
}

export interface Member extends User {
  role: Role;
  status: MemberStatus;
  pushEnabled: boolean;
  /** Only populated for managers, on the admin roster. */
  lastSeenAt?: number | null;
}

export interface Channel {
  id: string;
  name: string;
  type: ChannelType;
  topic: string | null;
  /** NULL means keep messages forever. */
  retentionDays: number | null;
  archivedAt: number | null;
  createdAt: number;
  /** The caller's own per-channel notification preference. */
  notify: NotifyPref;
  unreadCount: number;
  /** Present when unreadCount > 0 and the caller was @mentioned. */
  mentionCount: number;
  lastMessageAt: number | null;
  lastMessagePreview: string | null;
  /** For DMs: the other member. Null for standard/announcement channels. */
  peer: User | null;
  memberCount: number;
}

export interface Attachment {
  id: string;
  messageId: string;
  /** Opaque; the client never touches R2 directly on read. */
  r2Key: string;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  /** Worker-mediated download path. Membership is re-checked on every fetch. */
  url: string;
}

export interface Reaction {
  emoji: string;
  /** User ids, so the client can render "you reacted" without another round trip. */
  userIds: string[];
}

export interface Message {
  id: string;
  channelId: string;
  authorId: string;
  body: string;
  replyToId: string | null;
  /** Number of replies in this message's thread. */
  replyCount: number;
  createdAt: number;
  editedAt: number | null;
  deletedAt: number | null;
  pinnedAt: number | null;
  pinnedBy: string | null;
  attachments: Attachment[];
  reactions: Reaction[];
  /** User ids mentioned in the body. */
  mentions: string[];
  /** Count only — the full roster comes from GET /api/messages/:id/reads. */
  readCount: number;
  /** Set for join/leave/retention-change notices rendered as centred grey text. */
  systemKind: SystemKind | null;
}

export type SystemKind =
  'channel_created' | 'member_joined' | 'member_left' | 'retention_changed' | 'channel_archived';

/**
 * A pinned message, as listed by GET /api/channels/:id/pinned.
 *
 * Deliberately lighter than a full Message: the pinned list is a jump-off
 * point, so it needs enough to render a line and a link and nothing else.
 */
export interface PinnedMessage {
  id: string;
  body: string;
  authorId: string;
  createdAt: number;
  pinnedAt: number;
  pinnedBy: string;
}

/** The read-receipt roster behind the "seen by" sheet. */
export interface ReadReceipts {
  messageId: string;
  read: Array<{ user: User; readAt: number }>;
  unread: User[];
}

export interface PresenceUser {
  id: string;
  name: string;
}

/* ------------------------------------------------------------------ *
 * WebSocket wire protocol
 *
 * Client -> server frames are `ClientFrame`, server -> client are
 * `ServerFrame`. Both are discriminated on `t` and kept deliberately
 * short: this runs over flaky restaurant wifi on older Android phones,
 * and every byte is one the phone has to receive on a bad connection.
 * ------------------------------------------------------------------ */

export type ClientFrame =
  | {
      t: 'send';
      /** Client-generated id. Echoed back so an optimistic bubble can be reconciled. */
      cid: string;
      body: string;
      replyToId?: string | null;
      attachmentIds?: string[];
    }
  | { t: 'typing' }
  | { t: 'read'; upTo: number }
  | { t: 'ping' };

export type ServerFrame =
  | { t: 'hello'; you: string; presence: PresenceUser[]; hot: Message[] }
  | { t: 'msg'; msg: Message }
  | { t: 'ack'; cid: string; msg: Message }
  /**
   * A send was rejected. `error` is written to be shown to the sender as-is.
   *
   * `retryable` distinguishes "the network or the database hiccuped" from
   * "this will never be accepted" — an archived channel or a role that cannot
   * post here. Without it the client cannot tell them apart without matching
   * on English error text, and it burns five pointless retries on a message
   * that was never going to land.
   */
  | { t: 'nack'; cid: string; error: string; retryable: boolean }
  | { t: 'edit'; msg: Message }
  | { t: 'delete'; id: string }
  | { t: 'react'; id: string; reactions: Reaction[] }
  | { t: 'pin'; id: string; pinnedAt: number | null; pinnedBy: string | null }
  | { t: 'typing'; userId: string; name: string }
  | { t: 'presence'; presence: PresenceUser[] }
  | { t: 'read'; userId: string; upTo: number }
  /** Sent after the retention cron prunes this channel; clients drop cached messages. */
  | { t: 'purge'; before: number }
  | { t: 'pong' };

/* ------------------------------------------------------------------ *
 * REST payloads
 * ------------------------------------------------------------------ */

export interface HistoryPage {
  messages: Message[];
  /** Pass back as ?before= to page further into the past. Null when at the beginning. */
  nextCursor: number | null;
  /**
   * Pass back as ?after= to page toward the present. Only meaningful for a
   * page fetched with ?around=, since a normal page already ends at the newest
   * message. Null means this page reaches the present.
   */
  prevCursor: number | null;
}

export interface SearchHit {
  message: Message;
  channelId: string;
  channelName: string;
  /** Body with <mark> around matched terms. Already escaped; safe to render as HTML. */
  snippet: string;
}

export interface UploadTicket {
  attachmentId: string;
  /** Either a presigned R2 PUT url, or a worker-proxied fallback path. */
  uploadUrl: string;
  method: 'PUT' | 'POST';
  headers: Record<string, string>;
}

export interface RetentionPreview {
  /** How many messages the next cleanup run would delete under the proposed window. */
  affectedCount: number;
  /** Pinned messages are exempt; surfaced so the confirm copy can say so. */
  exemptPinnedCount: number;
}

export const RETENTION_PRESETS = [7, 30, 90, 365] as const;

/**
 * Windows offered by "clear messages".
 *
 * Minutes rather than days: the case this exists for is something posted in
 * the last few minutes that should not have been, and the person clearing it
 * wants a bounded blast radius rather than an empty channel.
 * `null` means everything.
 */
export const CLEAR_WINDOWS: Array<{ label: string; minutes: number | null }> = [
  { label: 'Last 30 minutes', minutes: 30 },
  { label: 'Last hour', minutes: 60 },
  { label: 'Last 24 hours', minutes: 60 * 24 },
  { label: 'Last 7 days', minutes: 60 * 24 * 7 },
  { label: 'Everything', minutes: null },
];

export interface ApiError {
  error: string;
  message: string;
}
